import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, X, BookOpen, MessageSquare, Scale, Loader2, FileText, Printer, RotateCcw, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { mockSOPs } from '../mock/mockData';
import { qaAPI } from '../services/api';

const QAScorer = () => {
  const { toast } = useToast();
  const [selectedSOPs, setSelectedSOPs] = useState([]);
  const [sopText, setSOPText] = useState('');
  const [chatTranscript, setChatTranscript] = useState('');
  const [agentName, setAgentName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [scorecard, setScorecard] = useState([
    { id: 1, name: 'Greeting & Introduction', max: 10, pass: 7 },
    { id: 2, name: 'Empathy & Tone', max: 20, pass: 14 },
    { id: 3, name: 'Problem Understanding', max: 20, pass: 14 },
    { id: 4, name: 'Resolution Quality', max: 25, pass: 18 },
    { id: 5, name: 'SOP Compliance', max: 15, pass: 12 },
    { id: 6, name: 'Closing & Follow-up', max: 10, pass: 7 },
  ]);
  const [nextId, setNextId] = useState(7);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showForm, setShowForm] = useState(true);

  const exportPDF = async () => {
    if (!sessionId) {
      toast({
        title: 'Error',
        description: 'No session ID found',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/qa-sessions/${sessionId}/export-pdf`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `QA_Report_${agentName.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        toast({
          title: 'PDF Downloaded!',
          description: 'QA report has been exported successfully',
        });
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: 'Export Failed',
        description: 'Failed to generate PDF report',
        variant: 'destructive'
      });
    }
  };


  const addSOP = (sopId) => {
    if (!selectedSOPs.includes(sopId)) {
      setSelectedSOPs([...selectedSOPs, sopId]);
    }
  };

  const removeSOP = (sopId) => {
    setSelectedSOPs(selectedSOPs.filter(id => id !== sopId));
  };

  const addParameter = () => {
    setScorecard([...scorecard, { id: nextId, name: '', max: 10, pass: 7 }]);
    setNextId(nextId + 1);
  };

  const removeParameter = (id) => {
    setScorecard(scorecard.filter(param => param.id !== id));
  };

  const updateParameter = (id, field, value) => {
    setScorecard(scorecard.map(param => 
      param.id === id ? { ...param, [field]: value } : param
    ));
  };

  const totalMax = scorecard.reduce((sum, param) => sum + (parseInt(param.max) || 0), 0);
  const totalClass = totalMax === 100 ? 'text-[#34c78a]' : totalMax > 100 ? 'text-[#f05252]' : 'text-[#f5a623]';

  const runQAScore = async () => {
    // Validation
    const sopContent = sopText || selectedSOPs.map(id => 
      mockSOPs.find(s => s.id === id)?.text
    ).filter(Boolean).join('\n\n');

    if (!sopContent) {
      toast({
        title: 'Missing SOP',
        description: 'Please select or paste an SOP to score against.',
        variant: 'destructive'
      });
      return;
    }

    if (!chatTranscript.trim()) {
      toast({
        title: 'Missing Transcript',
        description: 'Please paste the chat transcript to review.',
        variant: 'destructive'
      });
      return;
    }

    if (scorecard.length === 0 || !scorecard.every(p => p.name && p.max)) {
      toast({
        title: 'Invalid Scorecard',
        description: 'Please complete all scorecard parameters.',
        variant: 'destructive'
      });
      return;
    }

    setAnalyzing(true);
    toast({
      title: 'Analyzing...',
      description: 'AI is reviewing the chat transcript. This may take a few moments.',
    });

    try {
      // Call real Claude API for QA scoring
      const result = await qaAPI.create({
        agent_id: agentId || 'unknown',
        agent_name: agentName || 'Agent',
        agent_code: agentId || '',
        transcript: chatTranscript,
        sop_text: sopContent,
        sop_ids: selectedSOPs,
        scorecard: scorecard.map(p => ({
          name: p.name,
          max: parseInt(p.max) || 0,
          pass_score: parseInt(p.pass) || 0
        }))
      });

      setResults(result);
      setSessionId(result.id); // Save session ID for PDF export
      setAnalyzing(false);
      setShowForm(false);
      toast({
        title: 'Analysis Complete!',
        description: `Score: ${result.total_score}/${totalMax}`,
      });
    } catch (error) {
      console.error('QA scoring error:', error);
      setAnalyzing(false);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to analyze transcript. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setShowForm(true);
    setResults(null);
    setChatTranscript('');
    setAgentName('');
    setAgentId('');
  };

  if (!showForm && results) {
    return (
      <div className="min-h-screen bg-[#0d0f12] ml-56">
        <div className="max-w-4xl mx-auto p-6">
          {/* Score Display */}
          <Card className="bg-[#161920] border-[#2a3040] mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-8">
                {/* Score Ring */}
                <div className="relative w-24 h-24 flex-shrink-0">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#252b35"
                      strokeWidth="8"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke={results.total_score >= 80 ? '#34c78a' : results.total_score >= 60 ? '#f5a623' : '#f05252'}
                      strokeWidth="8"
                      strokeDasharray={`${(results.total_score / totalMax) * 251.2} 251.2`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-2xl font-bold text-white">{results.total_score}</div>
                    <div className="text-xs text-gray-500 font-mono">/{totalMax}</div>
                  </div>
                </div>

                {/* Score Info */}
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-1">{results.grade}</h3>
                  <p className="text-sm text-gray-400 mb-3">{results.summary}</p>
                  <div className="flex gap-2 flex-wrap">
                    {agentName && (
                      <Badge variant="outline" className="border-[#2a3040] text-gray-400">
                        👤 {agentName} {agentId && `· ${agentId}`}
                      </Badge>
                    )}
                    <Badge variant="outline" className="border-[#2a3040] text-gray-400">
                      📅 {new Date().toLocaleDateString()}
                    </Badge>
                    <Badge variant="outline" className="border-[#2a3040] text-gray-400">
                      📊 {scorecard.length} parameters
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Parameter Breakdown */}
          <div className="mb-4">
            <h2 className="text-sm font-mono tracking-wider text-gray-500 uppercase mb-3">
              Parameter Breakdown
            </h2>
          </div>
          <div className="space-y-3 mb-6">
            {results.parameters.map((param) => {
              const percentage = Math.round((param.scored / param.max) * 100);
              const borderColor = param.verdict === 'pass' ? 'border-l-[#34c78a]' : 
                                 param.verdict === 'partial' ? 'border-l-[#f5a623]' : 
                                 'border-l-[#f05252]';
              return (
                <Card key={param.name} className={`bg-[#161920] border-[#2a3040] border-l-2 ${borderColor}`}>
                  <CardContent className="pt-5 pb-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-white">{param.name}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-1.5 bg-[#252b35] rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-1000 ${
                              param.verdict === 'pass' ? 'bg-[#34c78a]' : 
                              param.verdict === 'partial' ? 'bg-[#f5a623]' : 
                              'bg-[#f05252]'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className={`text-base font-semibold font-mono ${
                          param.verdict === 'pass' ? 'text-[#34c78a]' : 
                          param.verdict === 'partial' ? 'text-[#f5a623]' : 
                          'text-[#f05252]'
                        }`}>
                          {param.scored}/{param.max}
                        </span>
                        <Badge className={`${
                          param.verdict === 'pass' ? 'bg-[#34c78a]/10 text-[#34c78a] border-[#34c78a]/30' : 
                          param.verdict === 'partial' ? 'bg-[#f5a623]/10 text-[#f5a623] border-[#f5a623]/30' : 
                          'bg-[#f05252]/10 text-[#f05252] border-[#f05252]/30'
                        } border`}>
                          {param.verdict === 'pass' ? '✓ Pass' : param.verdict === 'partial' ? '~ Partial' : '✗ Fail'}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{param.reason}</p>
                    {param.good_quote && (
                      <div className="bg-[#34c78a]/5 border-l-2 border-[#34c78a] rounded-r px-3 py-2 text-xs text-gray-400 italic">
                        ✓ "{param.good_quote}..."
                      </div>
                    )}
                    {param.bad_quote && (
                      <div className="bg-[#f05252]/5 border-l-2 border-[#f05252] rounded-r px-3 py-2 text-xs text-gray-400 italic">
                        ✗ "{param.bad_quote}..."
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* SOP Violations */}
          {results.sop_violations && results.sop_violations.length > 0 && (
            <Card className="bg-[#f05252]/5 border-[#f05252]/30 mb-6">
              <CardContent className="pt-5 pb-5">
                <div className="text-xs font-mono tracking-wider text-[#f05252] uppercase mb-3">
                  ⚠️ SOP Violations Found
                </div>
                <ul className="space-y-2">
                  {results.sop_violations.map((violation, idx) => (
                    <li key={idx} className="flex gap-2 text-sm text-gray-300">
                      <span className="text-[#f05252] flex-shrink-0">⚠</span>
                      <span>{violation}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Coaching Notes */}
          <Card className="bg-[#161920] border-[#2a3040] border-l-2 border-l-[#4f8ef7] mb-6">
            <CardContent className="pt-5 pb-5">
              <div className="text-xs font-mono tracking-wider text-[#4f8ef7] uppercase mb-3">
                Coaching Notes
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{results.coaching}</p>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={exportPDF}
              variant="outline"
              className="flex-1 border-[#2a3040] text-gray-400 hover:text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button
              onClick={resetForm}
              className="flex-1 bg-[#4f8ef7] hover:bg-[#4f8ef7]/90"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Score Another Chat
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0f12] ml-56">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <div className="px-3 py-1 rounded-full bg-[#34c78a]/10 border border-[#34c78a]/30 text-[#34c78a] text-xs font-mono tracking-wider">
              ✅ QUALITY ASSURANCE
            </div>
          </div>
          <h1 className="text-3xl font-semibold text-white mb-2">
            Score real chat transcripts<br />against your SOP
          </h1>
          <p className="text-gray-400">
            Upload a chat, define your scorecard, and get a detailed QA report with scores, violations, and coaching notes.
          </p>
        </div>

        {/* Step 1: SOP */}
        <div className="mb-4">
          <h2 className="text-sm font-mono tracking-wider text-gray-500 uppercase mb-3">
            Step 1 — Your SOP / Company Policy
          </h2>
        </div>
        <Card className="bg-[#161920] border-[#2a3040] mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#9b6dff]/10 border border-[#9b6dff]/30 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#9b6dff]" />
              </div>
              <div>
                <CardTitle className="text-white text-base">Select or paste your SOP</CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  The AI will score the chat against these specific policies. You can select multiple SOPs.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Select onValueChange={addSOP}>
              <SelectTrigger className="bg-[#1e2229] border-[#2a3040] text-white mb-3">
                <SelectValue placeholder="Select SOPs to include..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1e2229] border-[#2a3040]">
                {mockSOPs.map(sop => (
                  <SelectItem key={sop.id} value={sop.id} className="text-white">
                    {sop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedSOPs.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-4">
                {selectedSOPs.map(sopId => {
                  const sop = mockSOPs.find(s => s.id === sopId);
                  return (
                    <Badge key={sopId} variant="outline" className="border-[#9b6dff]/30 text-[#9b6dff]">
                      <FileText className="w-3 h-3 mr-1" />
                      {sop?.name}
                      <button onClick={() => removeSOP(sopId)} className="ml-2 hover:text-[#f05252]">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <div className="text-center text-xs text-gray-500 font-mono my-3">
              — OR PASTE —
            </div>

            <Textarea
              placeholder="Paste your SOP / policy text here...

Example: Agents must greet customers by name. Refunds are allowed within 30 days. Always offer a callback option if issue is unresolved..."
              value={sopText}
              onChange={(e) => setSOPText(e.target.value)}
              className="bg-[#1e2229] border-[#2a3040] text-white min-h-[100px]"
            />
          </CardContent>
        </Card>

        {/* Step 2: Chat Transcript */}
        <div className="mb-4">
          <h2 className="text-sm font-mono tracking-wider text-gray-500 uppercase mb-3">
            Step 2 — Chat Transcript to Review
          </h2>
        </div>
        <Card className="bg-[#161920] border-[#2a3040] mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#4f8ef7]/10 border border-[#4f8ef7]/30 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#4f8ef7]" />
              </div>
              <div>
                <CardTitle className="text-white text-base">Paste the chat transcript</CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  Copy from Zendesk, Intercom, Freshdesk, or any chat tool
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Paste the chat transcript here...

Example format:
Agent: Hi there! How can I help you today?
Customer: I want a refund for my order #12345
Agent: I can help with that. Can you share the order date?
Customer: It was 3 weeks ago
Agent: Our policy allows refunds within 30 days so you're covered..."
              value={chatTranscript}
              onChange={(e) => setChatTranscript(e.target.value)}
              className="bg-[#1e2229] border-[#2a3040] text-white min-h-[200px]"
            />

            <div className="mt-4">
              <div className="text-xs font-mono tracking-wider text-gray-500 uppercase mb-2">
                Agent Details (optional — for the report)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Agent name (e.g. Priya S.)"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  className="bg-[#1e2229] border-[#2a3040] text-white"
                />
                <Input
                  placeholder="Agent ID / Team (e.g. CS-042)"
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  className="bg-[#1e2229] border-[#2a3040] text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Scorecard */}
        <div className="mb-4">
          <h2 className="text-sm font-mono tracking-wider text-gray-500 uppercase mb-3">
            Step 3 — Build your scorecard
          </h2>
        </div>
        <Card className="bg-[#161920] border-[#2a3040] mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#f5a623]/10 border border-[#f5a623]/30 flex items-center justify-center">
                <Scale className="w-5 h-5 text-[#f5a623]" />
              </div>
              <div>
                <CardTitle className="text-white text-base">Define your QA parameters</CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  Add each parameter, set max points, and optionally set a passing threshold
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Column Headers */}
            <div className="grid grid-cols-[1fr_90px_90px_40px] gap-2 mb-2 px-1">
              <span className="text-[10px] font-mono tracking-wider text-gray-500 uppercase">Parameter name</span>
              <span className="text-[10px] font-mono tracking-wider text-gray-500 uppercase text-center">Max pts</span>
              <span className="text-[10px] font-mono tracking-wider text-gray-500 uppercase text-center">Pass at</span>
              <span></span>
            </div>

            {/* Parameters */}
            <div className="space-y-2 mb-4">
              {scorecard.map((param) => (
                <div key={param.id} className="grid grid-cols-[1fr_90px_90px_40px] gap-2">
                  <Input
                    placeholder="Parameter name"
                    value={param.name}
                    onChange={(e) => updateParameter(param.id, 'name', e.target.value)}
                    className="bg-[#1e2229] border-[#2a3040] text-white"
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={param.max}
                    onChange={(e) => updateParameter(param.id, 'max', parseInt(e.target.value) || 0)}
                    className="bg-[#1e2229] border-[#2a3040] text-white text-center font-mono"
                    min="1"
                  />
                  <Input
                    type="number"
                    placeholder="Pass"
                    value={param.pass}
                    onChange={(e) => updateParameter(param.id, 'pass', parseInt(e.target.value) || 0)}
                    className="bg-[#1e2229] border-[#2a3040] text-white text-center font-mono"
                    min="0"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeParameter(param.id)}
                    className="text-gray-500 hover:text-[#f05252] hover:bg-[#f05252]/10"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={addParameter}
              className="w-full border-dashed border-[#2a3040] text-gray-400 hover:text-white hover:border-[#4f8ef7]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add parameter
            </Button>

            <div className="flex items-center justify-between bg-[#1e2229] border border-[#2a3040] rounded-lg px-4 py-3 mt-4">
              <span className="text-sm text-gray-400">Total possible score</span>
              <span className={`text-base font-semibold font-mono ${totalClass}`}>
                {totalMax} pts
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Run Analysis Button */}
        <Button
          onClick={runQAScore}
          disabled={analyzing}
          className="w-full bg-[#34c78a] hover:bg-[#34c78a]/90 text-white py-6 text-base"
        >
          {analyzing ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Analyzing chat...
            </>
          ) : (
            <>
              ✅ Run QA Score
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default QAScorer;
